import React from 'react'
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import { Box, useTheme, Typography, List, ListItem, Chip } from "@mui/material";
import { tokens } from "../../../theme";
import Axios from 'axios';
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { getAuthHeader } from '../../../utils/authToken';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QuizIcon from '@mui/icons-material/Quiz';

const grid = 8;

const getItemStyle = (isDragging, draggableStyle, colors) => ({
    userSelect: "none",
    padding: `${grid}px ${grid * 2}px`,
    margin: `0 0 ${grid}px 0`,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: isDragging ? colors.greenAccent[600] : colors.primary[700],
    border: `1px solid ${colors.primary[100]}`,
    ...draggableStyle
});

const getListStyle = (isDraggingOver, colors) => ({
    border: isDraggingOver ? `2px dashed ${colors.greenAccent[500]}` : `2px solid ${colors.primary[100]}`,
    borderRadius: 4,
    padding: grid,
    backgroundColor: colors.primary[600],
});

const ReorderHomeworkPageModal = ({
    courseId,
    homeworks,
    homeworkTests,
    homeworkPageOrder,
    setSuccessAlertOpen,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertMessage,
    setBackdropOpen,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [itemList, setItemList] = React.useState([]);
    const [itemMap, setItemMap] = React.useState({});

    // for modal
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => { setOpen(false); };

    const handleSave = async (e) => {
        e.preventDefault();
        setBackdropOpen(true);

        const orderedItems = itemList.map((key) => {
            const item = itemMap[key];
            return { item_type: item._type, item_id: item.id };
        });

        await Axios.put(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/homework-page/reorder`, {
            orderedItems,
        }, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then(() => {
            setSuccessAlertMessage("Homework page order saved successfully");
            setSuccessAlertOpen(true);
            handleClose();
            window.location.reload();
        }).catch((error) => {
            setErrorAlertMessage(error.response?.data?.error || "Error: Cannot reorder items. Please try again.");
            setErrorAlertOpen(true);
        }).finally(() => {
            setBackdropOpen(false);
        });
    };

    React.useEffect(() => {
        if (!open) return;

        const combined = [
            ...(homeworks || []).map(hw => ({ ...hw, _type: 'homework', _key: `homework-${hw.id}` })),
            ...homeworkTests.map(test => ({ ...test, _type: 'v2_test', _key: `v2_test-${test.id}` })),
        ];

        // Sort using same logic as parent page
        combined.sort((a, b) => {
            if (homeworkPageOrder && homeworkPageOrder.length > 0) {
                const aOrder = homeworkPageOrder.find(
                    o => o.item_type === a._type && o.item_id === a.id
                );
                const bOrder = homeworkPageOrder.find(
                    o => o.item_type === b._type && o.item_id === b.id
                );
                if (aOrder && bOrder) return aOrder.order - bOrder.order;
                if (aOrder && !bOrder) return -1;
                if (!aOrder && bOrder) return 1;
            }
            return new Date(b.due_date) - new Date(a.due_date);
        });

        const map = {};
        const list = [];
        for (const item of combined) {
            map[item._key] = item;
            list.push(item._key);
        }

        setItemMap(map);
        setItemList(list);
    }, [homeworks, homeworkTests, homeworkPageOrder, open]);

    const handleOnDragEnd = (result) => {
        const { destination, source } = result;

        if (!destination) return;

        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return;
        }

        const items = Array.from(itemList);
        const [removed] = items.splice(source.index, 1);
        items.splice(destination.index, 0, removed);

        setItemList(items);
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        maxHeight: "80vh",
        overflowY: "auto",
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        borderRadius: '8px',
    };

    const totalItems = (homeworks?.length || 0) + homeworkTests.length;

    return (
        <div>
            <Button
                type="button"
                variant="contained"
                onClick={handleOpen}
                style={{
                    backgroundColor: colors.primary[300],
                    color: "white",
                    width: "14rem",
                    marginTop: "0.5rem",
                    minHeight: "3rem"
                }}
                size='small'
            >
                Reorder Homework
            </Button>
            <Modal
                aria-labelledby="reorder-homework-modal"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Typography variant="h6" fontWeight="bold" color={colors.primary[450]}>
                            Reorder Homework & Tests
                        </Typography>
                        <Typography variant="body2" color={colors.primary[300]} mt={1}>
                            Drag and drop to reorder items on the homework page.
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={2} width="100%" pt={2}>
                            <Box display="flex" flexDirection="column">
                                {totalItems === 0 ? (
                                    <Typography color={colors.primary[450]}>No items available.</Typography>
                                ) : (
                                    <DragDropContext onDragEnd={handleOnDragEnd}>
                                        <Droppable
                                            droppableId="droppable"
                                            renderClone={(provided, snapshot) => (
                                                <ListItem
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps.style,
                                                        colors
                                                    )}
                                                >
                                                    <DragIndicatorIcon />
                                                    Item
                                                </ListItem>
                                            )}
                                        >
                                            {(provided, snapshot) => (
                                                <List
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    style={getListStyle(snapshot.isDraggingOver, colors)}
                                                >
                                                    {itemList.map((itemKey, index) => {
                                                        const item = itemMap[itemKey];

                                                        return (
                                                            <Draggable
                                                                key={itemKey}
                                                                draggableId={itemKey}
                                                                index={index}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <ListItem
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        style={getItemStyle(
                                                                            snapshot.isDragging,
                                                                            provided.draggableProps.style,
                                                                            colors
                                                                        )}
                                                                    >
                                                                        <DragIndicatorIcon sx={{ color: colors.primary[300] }} />
                                                                        {item?._type === 'homework' ? (
                                                                            <AssignmentIcon sx={{ color: colors.blueAccent[400], fontSize: '1.2rem' }} />
                                                                        ) : (
                                                                            <QuizIcon sx={{ color: colors.greenAccent[400], fontSize: '1.2rem' }} />
                                                                        )}
                                                                        <Typography sx={{ wordBreak: 'break-word', flex: 1 }}>
                                                                            {item?.title}
                                                                        </Typography>
                                                                        <Chip
                                                                            label={item?._type === 'homework' ? 'HW' : 'Test'}
                                                                            size="small"
                                                                            sx={{
                                                                                backgroundColor: item?._type === 'homework'
                                                                                    ? colors.blueAccent[700]
                                                                                    : colors.greenAccent[700],
                                                                                color: 'white',
                                                                                fontSize: '0.7rem',
                                                                            }}
                                                                        />
                                                                    </ListItem>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </List>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                            </Box>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                sx={{ width: "fit-content" }}
                                onClick={handleSave}
                                disabled={totalItems === 0}
                            >
                                Save Order
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </div>
    )
}

export default ReorderHomeworkPageModal
